import { Injectable, inject } from '@angular/core';
import { ModalsService } from '@app/modules/ui/modals/modals.service';
import { of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import CONSTANTS from '@constants';

import { IFBBrandedRegistrationText, IFBMapWithLocaleKeys } from '@interfaces';

import { CompanyResource } from '@app/shared/modules/resources/company.resource';
import { CompanyService } from '@app/shared/services/company.service';
import { InvitationResource } from '@app/shared/modules/resources/invitation.resource';
import { TranslateService } from '@app/shared/modules/translate/services/translate.service';

import includes from 'lodash-es/includes';

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  private companyResource = inject(CompanyResource);
  private companyService = inject(CompanyService);
  private invitationResource = inject(InvitationResource);
  private translateService = inject(TranslateService);

  getRegistrationLink() {
    return this.companyResource.getRegistrationLink().pipe(
      map(response => (response && response.enabled ? response : null)),
      catchError(() => of(null))
    );
  }

  getCompany() {
    return this.companyResource.getCompany(this.companyService.subdomain).pipe(catchError(() => of(null)));
  }

  requestRegistrationEmail(email: string) {
    const body = {
      email,
      locale: this.translateService.getLocale(),
      role: CONSTANTS.USER_ROLES.TALENT_SCOUT
    };

    return this.invitationResource.saveSingle(body);
  }

  isDomainBlocked(domains: string[] = [], email: string): boolean {
    if (!email) {
      return false;
    }

    const emailDomain = email.indexOf('@') > -1 ? email.split('@')[1] : undefined;

    if (domains.length === 0 || !emailDomain) {
      return false;
    }

    const isProvidedDomainAllowed = includes(domains, emailDomain) || includes(domains, '*');
    return !isProvidedDomainAllowed;
  }

  allowedDomainsAsList(domains: string[] = []): string {
    return domains.join(', ');
  }

  getLocalizedInterpolatedBrandingTexts(
    details: IFBMapWithLocaleKeys<IFBBrandedRegistrationText> = {}
  ): IFBBrandedRegistrationText {
    // Populate branding texts
    let locale = this.translateService.getLocale();

    if (!CONSTANTS.DEFAULT_REGISTRATION_TEXTS[locale]) {
      locale = 'en_US';
    }

    const localizedDetails: IFBBrandedRegistrationText = details[locale] || ({} as IFBBrandedRegistrationText);
    const defaults = CONSTANTS.DEFAULT_REGISTRATION_TEXTS;

    const prop = 'headingText';
    if (!localizedDetails[prop]) {
      const overriddenLocale = this.translateService.overrideLocale(this.translateService.getLocale());
      localizedDetails[prop] = this.interpolate(defaults[overriddenLocale][prop]);
    }

    return localizedDetails;
  }

  private interpolate(s: string) {
    return s.replace('{{company}}', this.companyService.data.name);
  }
}
